import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { ErrorModel } from '@shared/model/error/error.model';
import { OrderModel } from '../../model/order.model';
import { OrderActions, OrderActionTypes } from '../actions/order.actions';

export interface State extends EntityState<OrderModel> {
  loading: boolean;
  updating: boolean;
  selected: string;
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  error: ErrorModel | ErrorModel[];
}

export const adapter: EntityAdapter<OrderModel> = createEntityAdapter<OrderModel>({
  selectId: (orderModel: OrderModel) => orderModel.uuid
});

export const initialState: State = adapter.getInitialState({
  loading: false,
  updating: false,
  selected: undefined,
  currentPage: undefined,
  totalPages: undefined,
  totalRecords: undefined,
  error: undefined
});

export function reducer(state = initialState, action: OrderActions): State {
  switch (action.type) {
    case OrderActionTypes.LoadOrderCollection:
    case OrderActionTypes.LoadOrder: {
      return {
        ...state,
        loading: true
      };
    }

    case OrderActionTypes.UpsertOrder:
    case OrderActionTypes.DeleteOrder: {
      return {
        ...state,
        updating: true
      };
    }

    case OrderActionTypes.LoadOrderCollectionSuccess: {
      const { collection, preserveExistingRecords } = action.payload;
      const method = preserveExistingRecords ? 'addMany' : 'setAll';

      return adapter[method](collection.records, {
        ...state,
        loading: false,
        totalRecords: collection.total_records,
        totalPages: collection.total_pages,
        currentPage: collection.current_page
      });
    }

    case OrderActionTypes.LoadOrderSuccess: {
      const { order } = action.payload;

      return adapter.upsertOne(order, { ...state, selected: order.uuid, loading: false });
    }

    case OrderActionTypes.UpsertOrderSuccess: {
      const { order } = action.payload;

      return adapter.upsertOne(order, { ...state, updating: false, selected: order.uuid });
    }

    case OrderActionTypes.DeleteOrderSuccess: {
      const { records } = action.payload;

      if (Array.isArray(records)) {
        const ids = records.map(record => record.uuid);
        return adapter.removeMany(ids, { ...state, updating: false });
      } else {
        const id = records.uuid;
        return adapter.removeOne(id, { ...state, updating: false, selected: undefined });
      }
    }

    case OrderActionTypes.LoadOrderCollectionFailure:
    case OrderActionTypes.LoadOrderFailure:
    case OrderActionTypes.UpsertOrderFailure:
    case OrderActionTypes.DeleteOrderFailure: {
      const { error } = action.payload;
      return { ...state, loading: false, updating: false, error };
    }

    case OrderActionTypes.ClearOrderState: {
      return initialState;
    }

    default: {
      return state;
    }
  }
}

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();
